import React, { useEffect, useState } from 'react';
import { useToast } from '../../sectepe-ui/index';
import leadSyncService, {
  GenerateIcebreakersInput,
  GenerateIcebreakersResult,
  Lead,
} from '../../services/leadSyncService';
import gtmService, { Icp, Offer, Persona } from '../../services/gtmService';
import { EmptyState } from '../../components/ui';
import { Icon } from '../../components/icons';
import { datum, fehlerText } from '../format';
import { ErgebnisTabelle } from './ErgebnisTabelle';
import { leadName } from './leadStatus';

interface Props {
  leads: Lead[];
  ausgewaehlt: string[];
  onGeladen: () => Promise<void> | void;
  onFehler: (text: string | null) => void;
}

type Kanal = 'email' | 'linkedin';
type Quelle = 'research' | 'website' | 'signal';

const QUELLEN: { value: Quelle; label: string }[] = [
  { value: 'research', label: 'Web-Research (Perplexity)' },
  { value: 'website', label: 'Website-Scraping' },
  { value: 'signal', label: 'LinkedIn-Post (Signal)' },
];

const STANDARD: GenerateIcebreakersInput = {
  channels: ['email', 'linkedin'],
  autoSources: ['website'],
  language: 'de',
  tone: 'warm, professionell, auf Augenhöhe',
  goal: 'Ein kurzes, unverbindliches Gespräch vereinbaren.',
  variantCount: 2,
};

const hatIcpStandard = (icp?: Icp) => !!icp && !!(
  icp.icebreakerTone || icp.icebreakerLanguage || icp.icebreakerGoal || icp.icebreakerChannels || icp.icebreakerAutoSources
);

/**
 * Wendet die auf einer ICP gespeicherten Icebreaker-Standards auf die
 * Optionen an. channels/autoSources sind kommasepariert; leere Felder lassen
 * den Wert unveraendert.
 */
export const icpStandardAnwenden = (icp: Icp | undefined, optionen: GenerateIcebreakersInput): GenerateIcebreakersInput => {
  if (!icp) return optionen;
  const csv = (v?: string) => (v || '').split(',').map((s) => s.trim()).filter(Boolean);
  const channels = csv(icp.icebreakerChannels) as Kanal[];
  const autoSources = csv(icp.icebreakerAutoSources) as Quelle[];
  return {
    ...optionen,
    icpId: icp.id,
    ...(icp.icebreakerTone ? { tone: icp.icebreakerTone } : {}),
    ...(icp.icebreakerLanguage ? { language: icp.icebreakerLanguage } : {}),
    ...(icp.icebreakerGoal ? { goal: icp.icebreakerGoal } : {}),
    ...(channels.length ? { channels } : {}),
    ...(autoSources.length ? { autoSources } : {}),
  };
};

/**
 * Icebreaker (Clay-Ersatz): personalisierte Einleitungen fuer die
 * ausgewaehlten Leads. Sie werden am Lead gespeichert und beim Lemlist-Abgleich
 * als {{icebreaker}}, {{icebreakerSubject}} und {{icebreakerLinkedin}} uebergeben.
 */
export const IcebreakerPanel: React.FC<Props> = ({ leads, ausgewaehlt, onGeladen, onFehler }) => {
  const { push } = useToast();
  const [optionen, setOptionen] = useState<GenerateIcebreakersInput>(STANDARD);
  const [icps, setIcps] = useState<Icp[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [angebote, setAngebote] = useState<Offer[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState<GenerateIcebreakersResult | null>(null);

  useEffect(() => {
    let aktiv = true;
    Promise.all([gtmService.getIcps(), gtmService.getPersonas(), gtmService.getOffers()])
      .then(([i, p, o]) => {
        if (!aktiv) return;
        setIcps(i ?? []);
        setPersonas(p ?? []);
        setAngebote(o ?? []);
      })
      .catch(() => {
        // GTM-Erdung ist optional.
      });
    return () => { aktiv = false; };
  }, []);

  const mitIcebreaker = leads.filter((l) => l.icebreakerGeneratedAt);
  const icp = icps.find((i) => i.id === optionen.icpId);

  const umschalten = <T extends string>(liste: T[] | undefined, wert: T): T[] => {
    const aktuell = liste || [];
    return aktuell.includes(wert) ? aktuell.filter((x) => x !== wert) : [...aktuell, wert];
  };

  const erzeugen = async () => {
    if (!optionen.channels || optionen.channels.length === 0) {
      onFehler('Bitte mindestens einen Kanal wählen.');
      return;
    }
    setLaeuft(true);
    setErgebnis(null);
    onFehler(null);
    try {
      const r = await leadSyncService.generateIcebreakers(ausgewaehlt, optionen);
      setErgebnis(r);
      push({
        message: `${r.generated} Icebreaker generiert${r.failed ? `, ${r.failed} fehlgeschlagen` : ''}.`,
        severity: r.failed ? 'warning' : 'success',
      });
      await onGeladen();
    } catch (e) {
      onFehler(fehlerText(e, 'Fehler bei der Icebreaker-Generierung'));
    } finally {
      setLaeuft(false);
    }
  };

  const icpStandardSpeichern = async () => {
    if (!optionen.icpId) return;
    try {
      await gtmService.updateIcp(optionen.icpId, {
        icebreakerTone: optionen.tone || '',
        icebreakerLanguage: optionen.language || '',
        icebreakerGoal: optionen.goal || '',
        icebreakerChannels: (optionen.channels || []).join(','),
        icebreakerAutoSources: (optionen.autoSources || []).join(','),
      });
      setIcps(await gtmService.getIcps());
      push({ message: 'Icebreaker-Standard für diese ICP gespeichert.', severity: 'success' });
    } catch (e) {
      onFehler(fehlerText(e, 'Fehler beim Speichern der ICP-Standards'));
    }
  };

  if (leads.length === 0) {
    return <EmptyState compact icon="sparkles" title="Keine Leads für Icebreaker" description="Erst Leads importieren oder anlegen, dann hier personalisierte Einleitungen erzeugen." />;
  }

  return (
    <div className="lead-panel">
      <section className="card" aria-label="Icebreaker generieren">
        <h3 className="card-title">Icebreaker generieren</h3>
        <p className="card-description">
          Personalisierte Einleitungen für die ausgewählten Leads – selbst angereichert statt über Clay. Sie werden am
          Lead gespeichert und beim Lemlist-Abgleich als <code>{'{{icebreaker}}'}</code>, <code>{'{{icebreakerSubject}}'}</code> und{' '}
          <code>{'{{icebreakerLinkedin}}'}</code> übergeben.
        </p>

        <fieldset className="lead-panel__feld">
          <legend>Kanäle</legend>
          <div className="btn-group">
            {(['email', 'linkedin'] as Kanal[]).map((k) => (
              <label key={k}>
                <input
                  type="checkbox"
                  checked={(optionen.channels || []).includes(k)}
                  onChange={() => setOptionen({ ...optionen, channels: umschalten(optionen.channels, k) })}
                />{' '}
                {k === 'email' ? 'E-Mail' : 'LinkedIn'}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="lead-panel__feld">
          <legend>Anreicherungs-Quellen (je Lead)</legend>
          <div className="btn-group" style={{ flexWrap: 'wrap' }}>
            {QUELLEN.map((q) => (
              <label key={q.value}>
                <input
                  type="checkbox"
                  checked={(optionen.autoSources || []).includes(q.value)}
                  onChange={() => setOptionen({ ...optionen, autoSources: umschalten(optionen.autoSources, q.value) })}
                />{' '}
                {q.label}
              </label>
            ))}
          </div>
          <small className="text-xs text-secondary">
            Web-Research kostet je Lead einen API-Aufruf und dauert länger. Für große Listen ggf. nur Website/Signal aktivieren.
          </small>
        </fieldset>

        <div className="akte-felder">
          <label>
            Varianten je Lead (A/B)
            <select value={optionen.variantCount ?? 1} onChange={(e) => setOptionen({ ...optionen, variantCount: Number(e.target.value) })}>
              <option value={1}>1 (schnell)</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </label>
          <label>
            Sprache
            <select value={optionen.language || 'de'} onChange={(e) => setOptionen({ ...optionen, language: e.target.value })}>
              <option value="de">Deutsch</option>
              <option value="en">Englisch</option>
            </select>
          </label>
          <label>
            Angebot (GTM, optional)
            <select value={optionen.offerId || ''} onChange={(e) => setOptionen({ ...optionen, offerId: e.target.value || undefined })}>
              <option value="">– keins –</option>
              {angebote.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <label>
            ICP (GTM, optional)
            <select
              value={optionen.icpId || ''}
              onChange={(e) => {
                const id = e.target.value || undefined;
                // Beim Wechsel auf eine ICP deren gespeicherte Standards uebernehmen.
                setOptionen(id
                  ? icpStandardAnwenden(icps.find((i) => i.id === id), { ...optionen, icpId: id })
                  : { ...optionen, icpId: undefined });
              }}
            >
              <option value="">– keine –</option>
              {icps.map((i) => <option key={i.id} value={i.id}>{i.name}{hatIcpStandard(i) ? ' ✓' : ''}</option>)}
            </select>
          </label>
          <label>
            Persona (GTM, optional)
            <select value={optionen.personaId || ''} onChange={(e) => setOptionen({ ...optionen, personaId: e.target.value || undefined })}>
              <option value="">– keine –</option>
              {personas.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label>
            Ton
            <input type="text" value={optionen.tone || ''} onChange={(e) => setOptionen({ ...optionen, tone: e.target.value })} />
          </label>
        </div>
        {icp && (
          <p className="text-xs text-secondary">
            {hatIcpStandard(icp)
              ? 'Standards dieser ICP übernommen (✓). Anpassen und neu speichern.'
              : 'Noch keine Standards für diese ICP – aktuelle Optionen speichern.'}{' '}
            <button type="button" className="btn btn-sm btn-ghost" onClick={icpStandardSpeichern}>Als ICP-Standard speichern</button>
          </p>
        )}
        <label className="lead-panel__feld">
          Ziel / CTA
          <input type="text" value={optionen.goal || ''} onChange={(e) => setOptionen({ ...optionen, goal: e.target.value })} />
        </label>

        <button type="button" className="btn btn-primary" onClick={erzeugen} disabled={laeuft || ausgewaehlt.length === 0}>
          <Icon name="sparkles" size={14} /> {laeuft ? 'Generiere…' : `Für ${ausgewaehlt.length} Leads generieren`}
        </button>
        {ausgewaehlt.length === 0 && <p className="text-xs text-secondary">Oben in der Liste Leads auswählen.</p>}
      </section>

      {ergebnis && (
        <ErgebnisTabelle
          titel="Ergebnis der Icebreaker-Generierung"
          gut={ergebnis.generated}
          gutLabel="generiert"
          schlecht={ergebnis.failed}
          details={ergebnis.details.filter((d) => !d.success)}
        />
      )}

      {mitIcebreaker.length > 0 && (
        <section className="card" aria-label="Leads mit Icebreaker">
          <h3 className="card-title">Leads mit Icebreaker ({mitIcebreaker.length})</h3>
          <p className="card-description">Einzelne Icebreaker prüfen und anpassen: in der Leads-Liste den Lead bearbeiten.</p>
          <div className="table-container">
            <table className="table">
              <thead><tr><th>Lead</th><th>Betreff</th><th>Icebreaker (E-Mail)</th><th>generiert</th></tr></thead>
              <tbody>
                {mitIcebreaker.map((l) => (
                  <tr key={l.id}>
                    <td><strong>{leadName(l)}</strong><div className="text-xs text-secondary">{l.email}</div></td>
                    <td className="text-xs">{l.icebreakerSubject || '—'}</td>
                    <td className="text-xs" style={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>{l.icebreakerEmail || l.icebreakerLinkedin || '—'}</td>
                    <td className="text-sm">{datum(l.icebreakerGeneratedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};
